import { useTranslation } from "react-i18next";
import { PrimaryButton } from "../../../../../theme/buttons";
import { Menuitem } from "../../../../../interfaces/Security/menu";
import { GridValidRowModel } from "@mui/x-data-grid";
import { AlertColor, Autocomplete, Box, Button, TextField } from "@mui/material";
import { Control, Controller, ControllerRenderProps, FieldErrors, UseFormGetValues, UseFormTrigger } from "react-hook-form";
import { CompanyBankAccount } from "../../../../../interfaces/Security/companies";
import { FormsErrors } from "../../../../../hooks/Forms/FormsErrors";
import { Payment } from "../../../../../interfaces/Purchases/Payments/payments";
import { Dispatch, SetStateAction } from "react";
import { Currency } from "../../../../../interfaces/Commons/currencies";

interface HeaderPaymentProps {
  resourceFiscalSignature: Menuitem | undefined,
  isEdit: boolean,
  mySelectedRows: readonly GridValidRowModel[],
  showSnackBar: (message: string, severity: AlertColor) => void,
  hasAdvanceSalesInvoice: boolean,
  getValues: UseFormGetValues<Payment>,
  trigger: UseFormTrigger<Payment>,
  isLoadingCurrenciesValues: boolean,
  setDialogConfirmApplyPayment: Dispatch<SetStateAction<boolean>>,
  setSelectedRows?: Dispatch<SetStateAction<readonly GridValidRowModel[]>>,
  setDialogPayment: Dispatch<SetStateAction<boolean>>,
  setClickedButtonGoBack: Dispatch<SetStateAction<boolean>>,
  loadingPost: boolean,
  control: Control<Payment, any>,
  errors: FieldErrors<Payment>,
  comboCompanyBankAccounts: CompanyBankAccount[],
  defaultCompanyBankAccount: CompanyBankAccount | undefined,
  setDefaultCompanyBankAccount: Dispatch<SetStateAction<CompanyBankAccount | undefined>>,
  onChangeTotals: (myRows: readonly GridValidRowModel[], currencySupplierBankAccount?: string, myTypingExchangeRate?: number) => Promise<void>,
  currencySupplier: Currency | undefined,
  commissionParsed: number,
}

export const HeaderPayment = ({
  resourceFiscalSignature,
  isEdit,
  mySelectedRows,
  showSnackBar,
  hasAdvanceSalesInvoice,
  getValues,
  trigger,
  isLoadingCurrenciesValues,
  setDialogConfirmApplyPayment,
  setSelectedRows,
  setDialogPayment,
  setClickedButtonGoBack,
  loadingPost,
  control,
  errors,
  comboCompanyBankAccounts,
  defaultCompanyBankAccount,
  setDefaultCompanyBankAccount,
  onChangeTotals,
  currencySupplier,
  commissionParsed,
}: HeaderPaymentProps) => {
  const [t] = useTranslation("global");
  const { GetError } = FormsErrors();

  const onApplyPayment = () => {
    let hasOnlySelectedAdvanceInvoices = mySelectedRows?.findIndex((item) => !item.product_service_key) ?? -1;
    if (mySelectedRows.length === 0) {
      showSnackBar(t("payments.info.add-details"), "warning");
      return;
    } else if (hasOnlySelectedAdvanceInvoices === -1 && hasAdvanceSalesInvoice) {
      showSnackBar(t("payments.info.select-purchase-invoices"), "warning");
      return;
    }

    const numericValue = parseFloat(getValues("total_amount")?.toString() ?? "0");
    const roundedCommissionParsed = Math.round(commissionParsed * 100) / 100;
    if (numericValue <= roundedCommissionParsed) {
      showSnackBar(t("payments.info.minimum-commission-payment"), "warning");
      return false;
    }

    if ((getValues("difference_amount") ?? -1) < 0) {
      showSnackBar(t("payments.info.difference_negative"), "warning");
    } else {
      trigger().then((responseTrigger) => {
        if (!isLoadingCurrenciesValues && responseTrigger) setDialogConfirmApplyPayment(true);
      });
    }
  }

  const onGoBack = () => {
    setDialogPayment(false);
    setClickedButtonGoBack((prev) => !prev);
    setSelectedRows && setSelectedRows(() => []);
  }

  const onChangeCompanyBankAccount = async (field: ControllerRenderProps<Payment, "company_bank_account_id">, value: CompanyBankAccount | null) => {
    await field.onChange(value?.company_bank_account_id || null);
    setDefaultCompanyBankAccount(value ?? undefined);
    onChangeTotals(mySelectedRows, currencySupplier?.currency_code);
  }

  return (
    <div className="w-100 d-flex flex-row  align-items-center mt-2">
      <div className="w-50 mt-2">
        <h3>{t("payments.title-view")}</h3>
      </div>
      <div className="d-flex flex-row-reverse w-100">
        <PrimaryButton
          disabled={!resourceFiscalSignature?.create || isEdit}
          onClick={() => onApplyPayment()}
          sx={{ display: 'flex', alignItems: 'center', width: '25%' }}
          variant="outlined"
        >
          {t("paymentsdetails.buttons.apply-payment")}
        </PrimaryButton>
        <Button
          variant="outlined"
          onClick={() => onGoBack()}
          sx={{ marginInline: 1, display: 'flex', alignItems: 'center', width: '25%' }}
          disabled={loadingPost}
        >
          {t("generic.buttons.goback")}
        </Button>
        <div className="w-75">
          <Controller
            name="company_bank_account_id"
            control={control}
            rules={{ required: true }}
            render={({ field }) =>
              <Autocomplete
                ref={field.ref}
                size="small"
                options={comboCompanyBankAccounts}
                defaultValue={defaultCompanyBankAccount}
                getOptionLabel={(option) => `${option.account_alias} - ${option.bank_name} - ${option.currency_code}`}
                renderOption={(props, option: CompanyBankAccount) => (
                  <div key={option.company_bank_account_id}>
                    <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                      {option.account_alias} - {option.bank_name} - {option.currency_code}
                    </Box>
                  </div>
                )}
                sx={{ width: "100%" }}
                isOptionEqualToValue={(option, value) => option.company_bank_account_id === value.company_bank_account_id}
                onChange={(_, values) => onChangeCompanyBankAccount(field, values)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`${t("payments.fields.companybankaccount")}`}
                    variant="filled"
                    value={field.value}
                    error={!!errors.company_bank_account_id}
                    helperText={GetError(errors.company_bank_account_id?.type)}
                  />
                )}
                disabled={isEdit}
              />
            }
          />
        </div>
      </div>
    </div>
  )
}
